import { useCallback, useMemo, useEffect } from 'react'
import { useAtom } from 'jotai'
import { userInfoAtom, venueAtom, tabUuidAtom } from '../state/global'
import { IS_PROD } from '../utils/constants'

// Type declarations for mParticle global object
declare global {
  interface Window {
    mParticle: {
      ready: (callback: () => void) => void
      logPageView: (screenName: string, attributes: Record<string, any>) => void
      Rokt: {
        selectPlacements: (placement: { identifier: string; attributes?: Record<string, any> }) => Promise<any>
        isReady: () => boolean
      }
      Identity: {
        identify: (request: { userIdentities: { email: string } }) => void
        getCurrentUser: () => {
          setUserAttribute: (key: string, value: any) => void
        } | null
      }
    }
  }
}

interface RoktUserAttributes {
  email?: string
  firstname?: string
  lastname?: string
  phone?: string
  confirmationref?: string
  postcode?: string
  billingzipcode?: string
  currency?: string
  country?: string
  language?: string
}

interface UseRoktReturn {
  script: string
  setUserAttributes: (attributes: RoktUserAttributes) => void
  logPageView: (screenName?: string, attributes?: Record<string, any>) => void
  isReady: boolean
}

export const useRokt = (): UseRoktReturn => {
  const [user] = useAtom(userInfoAtom)
  const [venue] = useAtom(venueAtom)
  const [tabUuid] = useAtom(tabUuidAtom)
  // Generate the ROKT SDK script
  const script = useMemo(() => {
    return `
// Enter your Rokt API key
const API_KEY = "${venue?.rokt_api_key || ''}";

// Enter your custom subdomain if you are using a first-party domain configuration (optional)
const ROKT_DOMAIN = "${venue?.rokt_domain || 'https://apps.rokt-api.com'}";

window.mParticle = {
    config: {
       isDevelopmentMode: ${!IS_PROD},
       identifyRequest: {
           userIdentities: {
               email: ${user?.email ? `'${user.email}'` : 'null'},
           }
        }
    },
    // If the user is identified with their email address, set additional user attributes.
    identityCallback: function(result) {
        if (result.getUser() && ${user ? 'true' : 'false'}) {
            result.getUser().setUserAttribute('firstname', '${user?.first_name || ''}');
            result.getUser().setUserAttribute('lastname', '${user?.last_name || ''}');
            result.getUser().setUserAttribute('phone', '${user?.phone || ''}');
        }
    }
};

// Load the SDK
(function (e) {
  window.mParticle = window.mParticle || {};

  window.mParticle.EventType = {
    Unknown: 0,
    Navigation: 1,
    Location: 2,
    Search: 3,
    Transaction: 4,
    UserContent: 5,
    UserPreference: 6,
    Social: 7,
    Other: 8,
    Media: 9,
  };

  window.mParticle.eCommerce = { Cart: {} };
  window.mParticle.Identity = {};
  window.mParticle.Rokt = {};
  window.mParticle.config = window.mParticle.config || {};
  window.mParticle.config.rq = [];
  window.mParticle.config.snippetVersion = 2.7;

  window.mParticle.ready = function (cb) {
    window.mParticle.config.rq.push(cb);
  };

  [
    "endSession",
    "logError",
    "logBaseEvent",
    "logEvent",
    "logForm",
    "logLink",
    "logPageView",
    "setSessionAttribute",
    "setAppName",
    "setAppVersion",
    "setOptOut",
    "setPosition",
    "startNewSession",
    "startTrackingLocation",
    "stopTrackingLocation",
  ].forEach(function (fn) {
    window.mParticle[fn] = function () {
      var args = Array.prototype.slice.call(arguments);
      args.unshift(fn);
      window.mParticle.config.rq.push(args);
    };
  });

  ["setCurrencyCode", "logCheckout"].forEach(function (fn) {
    window.mParticle.eCommerce[fn] = function () {
      var args = Array.prototype.slice.call(arguments);
      args.unshift("eCommerce." + fn);
      window.mParticle.config.rq.push(args);
    };
  });

  ["identify", "login", "logout", "modify"].forEach(function (fn) {
    window.mParticle.Identity[fn] = function () {
      var args = Array.prototype.slice.call(arguments);
      args.unshift("Identity." + fn);
      window.mParticle.config.rq.push(args);
    };
  });

  [
    "selectPlacements",
    "hashAttributes",
    "setExtensionData",
    "use",
    "getVersion",
    "terminate",
  ].forEach(function (fn) {
    window.mParticle.Rokt[fn] = function () {
      var args = Array.prototype.slice.call(arguments);
      args.unshift("Rokt." + fn);
      window.mParticle.config.rq.push(args);
    };
  });

  var isDev = window.mParticle.config.isDevelopmentMode ? 1 : 0;
  var n = "?env=" + isDev;
  var dataPlan = window.mParticle.config.dataPlan;

  if (dataPlan) {
    var planId = dataPlan.planId,
      planVersion = dataPlan.planVersion;
    if (planId) {
      if (planVersion && (planVersion < 1 || planVersion > 1000)) {
        planVersion = null;
      }
      n += "&plan_id=" + planId + (planVersion ? "&plan_version=" + planVersion : "");
    }
  }

  var versions = window.mParticle.config.versions,
    versionParams = [];
  if (versions) {
    Object.keys(versions).forEach(function (key) {
      versionParams.push(key + "=" + versions[key]);
    });
  }

  var script = document.createElement("script");
  script.type = "text/javascript";
  script.async = true;

  window.ROKT_DOMAIN = ROKT_DOMAIN || "https://apps.rokt-api.com";
  mParticle.config.domain = ROKT_DOMAIN.split("//")[1];

  script.src =
    ROKT_DOMAIN +
    "/js/v2/" +
    e +
    "/app.js" +
    n +
    "&" +
    versionParams.join("&");

  var firstScript = document.getElementsByTagName("script")[0];
  firstScript.parentNode.insertBefore(script, firstScript);
})(API_KEY);
`
  }, [venue?.rokt_api_key, venue?.rokt_domain, user])

  const isWindowDefined = typeof window !== 'undefined'
  // Check if mParticle is ready
  const isReady = useMemo(() => {
    if (isWindowDefined && window.mParticle) {
      return window.mParticle.Rokt.isReady?.() === true
    }
    return false
  }, [isWindowDefined])

  // Set user attributes
  const setUserAttributes = useCallback(
    (attributes: RoktUserAttributes) => {
      if (!isReady) {
        console.warn('[ROKT] mParticle not ready yet')
        return
      }

      window.mParticle.ready(() => {
        try {
          // Update user identity if email is provided
          if (attributes.email) {
            window.mParticle.Identity.identify({
              userIdentities: {
                email: attributes.email,
              },
            })
          }

          // Set user attributes
          const user = window.mParticle.Identity.getCurrentUser()
          if (user) {
            Object.entries(attributes).forEach(([key, value]) => {
              if (value) {
                user.setUserAttribute(key, value)
              }
            })
          }
        } catch (error) {
          console.error('[ROKT] Error setting user attributes:', error)
        }
      })
    },
    [isReady]
  )

  // Auto-set user attributes when data changes
  const setDefaultUserAttributes = useCallback(() => {
    if (!isReady || !user || !venue) return

    const attributes: RoktUserAttributes = {
      email: user.email,
      firstname: user.first_name,
      lastname: user.last_name,
      phone: user.phone,
      postcode: venue.zipcode,
      confirmationref: tabUuid ? tabUuid.substring(0, 8) : undefined,
    }

    // Remove undefined values
    const cleanAttributes = Object.fromEntries(Object.entries(attributes).filter(([_, value]) => value !== undefined))

    if (Object.keys(cleanAttributes).length > 0) {
      setUserAttributes(cleanAttributes)
    }
  }, [isReady, user, venue, tabUuid, setUserAttributes])

  // Auto-set user attributes when mParticle is ready and data is available
  useEffect(() => {
    if (isReady && user && venue) {
      setDefaultUserAttributes()
    }
  }, [isReady, user, venue, tabUuid, setDefaultUserAttributes])

  // Log page view
  const logPageView = useCallback(
    (screenName = 'page view', attributes: Record<string, any> = {}) => {
      if (!isReady) {
        console.warn('[ROKT] mParticle not ready yet')
        return
      }

      window.mParticle.ready(() => {
        try {
          const pageViewAttributes = {
            screen_name: screenName,
            url: window.location.toString(),
            referring_page: document.referrer || 'direct',
            ...attributes,
          }

          window.mParticle.logPageView(screenName, pageViewAttributes)
        } catch (error) {
          console.error('[ROKT] Error logging page view:', error)
        }
      })
    },
    [isReady]
  )

  return {
    script,
    setUserAttributes,
    logPageView,
    isReady,
  }
}
